/**
 * What is running and what it consumed: provider account, current action, blockers,
 * task/workstream linkage, reported usage and its freshness. Every figure says what kind it is
 * (reported / estimated / not reported) and subscription headroom is a separate reading.
 */
import { formatAgo, MetricChip } from '@viko/transcript-ui'
import { CircleAlert, Gauge, Hammer, Link2, UserRound } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  type AccountRef,
  chargeLabel,
  formatTokens,
  headroomLabel,
  isStale,
  type SessionUsage,
  totalTokens,
} from '../lib/usage-accounting.js'
import { AccountDetails, CostDetails, FreshnessDetails, HeadroomDetails } from './UsageDetails.js'

function useNow(ms = 30_000) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms)
    return () => clearInterval(t)
  }, [ms])
  return now
}

/** The activity line is one plain-text row: flatten inline markdown (bold, code, links). */
export function plainInline(s: string): string {
  return s
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/`([^`]+)`/g, '$1')
}

export interface StripRun {
  activity?: string
  blockedOn?: string
  workstreamName?: string
  workstreamId?: string
  taskId?: string
  taskTitle?: string
  status?: string
}

export function UsageStrip({
  run,
  usage,
  account,
  awaiting = 0,
  compact = false,
  model,
}: {
  run?: StripRun
  usage: SessionUsage
  account?: AccountRef
  awaiting?: number
  compact?: boolean
  model?: string
}) {
  const now = useNow()
  const stale = isStale(usage.observedAt, now)
  const link = [run?.workstreamName ?? run?.workstreamId, run?.taskTitle ?? run?.taskId]
    .filter(Boolean)
    .join(' · ')
  const blocker = run?.blockedOn ?? (awaiting > 0 ? `${awaiting} waiting on you` : undefined)
  const tokens = usage.tokens ? totalTokens(usage.tokens) : undefined
  return (
    <section
      className="vk-usage-strip grid gap-1 border-b border-border-subtle bg-surface-2 px-4 py-2 text-[14px] leading-[20px] text-text-2 desktop:text-[14px]"
      aria-label="Run status and usage"
      data-testid="usage-strip"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <MetricChip
          label={`Provider account: ${account ? account.label : 'unknown'}`}
          title="Provider account"
          details={<AccountDetails {...(account ? { account } : {})} />}
        >
          <UserRound size={13} aria-hidden="true" />
          <span>
            {account
              ? `${account.label}${account.inferred ? ' (only account)' : ''}`
              : 'Account unknown'}
          </span>
        </MetricChip>
        {link ? (
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <Link2 size={13} aria-hidden="true" />
            <span className="truncate">
              <span className="sr-only">Linked work: </span>
              {link}
            </span>
          </span>
        ) : null}
      </div>
      {blocker ? (
        <div className="flex items-start gap-1.5 text-text-1">
          <CircleAlert size={13} className="mt-0.5 flex-none text-status-wait" aria-hidden="true" />
          <span>
            <span className="sr-only">Blocked on: </span>
            {blocker}
          </span>
        </div>
      ) : run?.activity ? (
        <div className="flex items-center gap-1.5">
          <Hammer size={13} className="flex-none text-text-3" aria-hidden="true" />
          <span className="truncate">
            <span className="sr-only">Current action: </span>
            {plainInline(run.activity)}
          </span>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <MetricChip
          label={`${chargeLabel(usage.charge)}${tokens !== undefined ? `, ${formatTokens(tokens)} tokens` : ''}`}
          title="Cost and tokens"
          testId="strip-cost"
          details={<CostDetails usage={usage} now={now} {...(model ? { model } : {})} />}
        >
          <span data-charge={usage.charge.kind}>
            {chargeLabel(usage.charge)}
            {tokens !== undefined
              ? ` · ${formatTokens(tokens)} tokens${usage.partial ? ' (subagents only)' : ''}`
              : ''}
          </span>
        </MetricChip>
        {!compact && usage.children.count > 0 ? (
          <span>
            {usage.children.count} subagent{usage.children.count === 1 ? '' : 's'} ·{' '}
            {formatTokens(usage.children.tokens)} tokens
            {usage.children.includedInTotal ? ' (included above)' : ''}
          </span>
        ) : null}
        <MetricChip
          label={headroomLabel(usage.headroom, now)}
          title="Subscription headroom"
          details={
            <HeadroomDetails
              headroom={usage.headroom}
              now={now}
              {...(account ? { account } : {})}
            />
          }
        >
          <span
            className="inline-flex items-center gap-1.5"
            data-headroom={usage.headroom ? 'known' : 'unknown'}
          >
            <Gauge size={13} aria-hidden="true" />
            {headroomLabel(usage.headroom, now)}
          </span>
        </MetricChip>
        <MetricChip
          label={
            usage.observedAt
              ? `Usage updated ${formatAgo(usage.observedAt, now)}`
              : 'No usage reported yet'
          }
          title="Usage freshness"
          details={<FreshnessDetails usage={usage} now={now} />}
        >
          {/* "Not reported yet" is not a problem: warn only when a report went stale. */}
          <span className={stale && usage.observedAt ? 'text-status-wait' : 'text-text-3'}>
            {usage.observedAt
              ? `Usage updated ${formatAgo(usage.observedAt, now)}${stale ? ' · may be out of date' : ''}`
              : 'No usage reported yet'}
          </span>
        </MetricChip>
      </div>
    </section>
  )
}
